import styled, { css } from 'styled-components';
import { always, equals, ifElse } from 'ramda';
import { motion } from 'framer-motion';

import { COLORS } from '@theme/colors';
import { renderWhenTrue } from '@utils/rendering';
import { media } from '@theme/media';
import { Button } from '@components/form';
import { Z_INDEX } from '@utils/constants';
import { NARROW_CONTAINER_WIDTH } from '@components/blocks';
import { LETTER_SPACING, typography } from '@theme/font';
import { OFFSET } from '@theme/helpers';

const filterFontStyles = css`
  ${typography({ spacing: LETTER_SPACING.S })};
`;

const optionIconStyles = css`
  content: '';
  background-color: ${COLORS.BLACK};
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
`;

const getOpenContainerStyles = renderWhenTrue(
  always(css`
    pointer-events: auto;
    overflow: auto;
  `)
);

export const Container = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  z-index: ${Z_INDEX.HEADER_FILTERS};
  pointer-events: none;
  overflow: hidden;
  ${({ open }: { open: boolean }) => getOpenContainerStyles(open)};

  ${media.tablet} {
    max-width: ${NARROW_CONTAINER_WIDTH}px;
  }
`;

export const TransformableContainer = styled(motion.div)`
  overflow: auto;
  background-color: ${COLORS.WHITE};
  padding: 0 14px 14px;
  height: calc(var(--vh) - 110px);

  ${media.tablet} {
    max-height: 80vh;
    padding: 0 14px 28px;
    padding: 0 14px 14px;
  }
`;

export const FilterMobileName = styled.div`
  padding: 23px 0 20px;
  position: relative;

  &:after {
    ${optionIconStyles};
    width: 7px;
    height: 1px;
  }

  &:before {
    ${optionIconStyles};
    width: 1px;
    height: 7px;
    right: 8px;
    display: ${({ open }: { open: boolean }) => (open ? 'none' : 'block')};
  }
`;

export const FilterItemContainer = styled.div`
  margin-bottom: 30px;

  ${media.tablet} {
    margin-bottom: 0;
    border-top: 1px solid ${COLORS.BLACK};
    display: grid;
    grid-template-columns: 132px 1fr;
    padding-top: 9px;
  }
`;

export const FilterName = styled.div`
  text-transform: none;
`;

export const FilterItemMobileContainer = styled.div`
  display: block;

  ${media.tablet} {
    display: none;
    margin-bottom: 30px;
  }
`;

export const FilterSpacer = styled.div`
  height: 58px;
`;

export const FilterOptionsContainer = styled.div`
  position: relative;
  border-top: 1px solid ${COLORS.BLACK};
  border-bottom: 1px solid ${COLORS.BLACK};
  max-height: 199px;
  overflow: auto;

  ${media.tablet} {
    border: none;
    max-height: none;
  }
`;

export const FilterColorsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12px, 1fr));
  grid-gap: 12px 22px;
  padding: 3px 0 12px;
`;

const getFilterMobileContainerStyles = ifElse(
  equals(true),
  always(css`
    height: auto;
    border-bottom: 1px solid ${COLORS.BLACK};
  `),
  always(css`
    button {
      display: none;
    }
    height: 0;
    border-bottom: none;
  `)
);

export const FilterOptionsMobileContainer = styled.div`
  position: relative;
  border-top: 1px solid ${COLORS.BLACK};
  ${({ open }: { open: boolean }) => getFilterMobileContainerStyles(open)};
`;

export const FiltersContainer = styled.div`
  background-color: ${COLORS.WHITE};
  margin-bottom: 58px;

  ${media.tablet} {
    margin-bottom: 0;
  }
`;

export const MobileFiltersContainer = styled.div`
  display: block;

  ${media.tablet} {
    display: none;
  }
`;

export const DesktopFiltersContainer = styled.div`
  display: none;

  ${media.tablet} {
    display: block;
    padding-bottom: 30px;
  }
`;

const selectedStyles = css`
  content: '';
  width: 11px;
  height: 7px;
  transform-origin: center center;
  transform: rotate(-45deg);
  border-bottom: 1px solid ${COLORS.BLACK};
  border-left: 1px solid ${COLORS.BLACK};
  position: absolute;
  right: 17px;

  ${media.desktop} {
    right: -17px;
  }
`;

const renderSelected = renderWhenTrue(
  always(css`
    ${media.desktop} {
      position: relative;
    }

    &:after {
      ${selectedStyles};
    }
  `)
);

const renderColorSelected = renderWhenTrue(
  always(css`
    &:after {
      ${selectedStyles};
      top: calc(50% - 4px);
      right: 3px;

      ${media.desktop} {
        right: 3px;
      }
    }
  `)
);

export const Option = styled.button`
  padding: 13px 0 7px;
  border-bottom: 1px solid ${COLORS.BLACK};
  display: block;
  width: 100%;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  ${media.tablet} {
    border-bottom: none;
    margin: 0 0 9px;
    padding: 0;
  }
`;

export const OptionText = styled.span`
  ${filterFontStyles};

  &:hover {
    border-bottom: 1px solid ${COLORS.BLACK40};
  }

  ${({ selected }: { selected: boolean }) => renderSelected(selected)};
`;

export const FilterTitle = styled.div`
  ${filterFontStyles};
  padding: ${OFFSET.S} 0 0;
  height: 44px;
  box-sizing: border-box;
  border-bottom: 1px solid ${COLORS.BLACK};
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: ${LETTER_SPACING.L};
  text-align: center;

  ${media.tablet} {
    display: none;
  }
`;

export const CloseButton = styled.button`
  ${filterFontStyles};
  position: absolute;
  right: 0;
  top: 0;
  width: 44px;
  height: 44px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1px 6px;

  ${media.tablet} {
    display: none;
  }

  &:after {
    content: '\\2715';
  }
`;

export const FilterActions = styled.div`
  display: flex;
  gap: 14px;
  position: sticky;
  bottom: 0;
  left: auto;
  width: 100%;

  &::after {
    content: '';
    height: 80px;
    width: 100%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.75) 10%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 1) 100%
    );
    display: block;
    position: absolute;
    bottom: -14px;
    z-index: -1;
  }
`;

interface ActionButtonProps {
  isApply?: boolean;
  hide?: boolean;
}

export const ActionButton = styled(Button)<ActionButtonProps>`
  text-transform: uppercase;
  display: ${({ hide }) => (hide ? 'none' : 'inline-block')};
  white-space: nowrap;
`;

export const OptionColor = styled.button.attrs({ type: 'button' })`
  display: inline-block;
  padding: 8px 22px 4px 3px;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  ${({ selected }: { selected: boolean }) => renderColorSelected(selected)};

  ${media.tablet} {
    padding: 0;
  }
`;

export const Color = styled.div<{ color: string }>`
  border-radius: 100%;
  background: ${({ color }) => (color ? color : null)};
  width: 16px;
  height: 16px;

  ${media.tablet} {
    width: 12px;
    height: 12px;
  }
`;

export const PriceInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  margin-bottom: 12px;

  > div {
    padding: 9px 12px;
  }
`;
